import React, { useState } from 'react';

import { ArrowDownSLine } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { getCurrencySymbol } from '@utils/utils';

import { AlternativeStayOptionsProps } from './types';
import { getPriceStartingFromLabel } from './discountHelpers';
import PackageTemplateResponsiveDropdown from './PackageTemplateResponsiveDropdown';
import { isPersonQuotaUnavailable } from './guestLimits';
import {
  DropdownSurface,
  MobileSelectionButton,
  MobileSelectionList,
  SelectionButton,
  SelectionChevron,
  SelectionField,
  SelectionFieldLabel,
  SelectionLabel,
  SelectionList,
  SelectionMeta,
  SelectionPriceBlock,
  SelectionPriceNote,
  SelectionPanel,
  SelectionTrigger,
  OccupiedBadge,
  SelectableItemContent,
} from './styled';

const formatPrice = (price?: number) => {
  if (!price) return { main: '0', decimal: ',00' };

  const priceStr = price.toFixed(2);
  const [main, decimal] = priceStr.split('.');

  return { main, decimal: `,${decimal}` };
};

const AlternativeStayOptionsList = ({
  embedded = false,
  hotels = [],
  selectedPackageDetailId,
  currencyCode,
  onStayOptionChange,
}: AlternativeStayOptionsProps) => {
  const [isHotelDropdownOpen, setIsHotelDropdownOpen] = useState(false);
  const currencySymbol = getCurrencySymbol(currencyCode || 'TL');

  if (hotels.length === 0) {
    return null;
  }

  const selectedHotel =
    hotels.find((item) => item.packageDetailId === selectedPackageDetailId) ||
    hotels[0];
  const renderHotelOptions = ({
    mobile,
    width = 0,
  }: {
    mobile: boolean;
    width?: number;
  }) => {
    const List = mobile ? MobileSelectionList : SelectionList;
    const OptionButton = mobile ? MobileSelectionButton : SelectionButton;
    const options = (
      <List role='listbox'>
        {hotels.map((item) => (
          <OptionButton
            key={item.packageDetailId}
            type='button'
            role='option'
            aria-selected={item.packageDetailId === selectedPackageDetailId}
            $selected={item.packageDetailId === selectedPackageDetailId}
            onClick={() => {
              onStayOptionChange?.(item.packageDetailId);
              setIsHotelDropdownOpen(false);
            }}
          >
            <SelectableItemContent>
              <SelectionLabel>{item.hotelName || 'Konaklama'}</SelectionLabel>
              {isPersonQuotaUnavailable(item) ? (
                <OccupiedBadge>Müsaitlik bulunmamaktadır.</OccupiedBadge>
              ) : null}
            </SelectableItemContent>
            {item.price !== undefined &&
            item.price !== null &&
            !isPersonQuotaUnavailable(item) ? (
              <SelectionPriceBlock>
                <SelectionMeta>
                  {formatPrice(item.price).main}
                  {formatPrice(item.price).decimal} {currencySymbol}
                </SelectionMeta>
                <SelectionPriceNote>
                  {getPriceStartingFromLabel(currencyCode || 'TL')}
                </SelectionPriceNote>
              </SelectionPriceBlock>
            ) : null}
          </OptionButton>
        ))}
      </List>
    );

    if (mobile) return options;

    return <DropdownSurface $width={width}>{options}</DropdownSurface>;
  };

  return (
    <SelectionPanel $embedded={embedded}>
      <PackageTemplateResponsiveDropdown
        isOpen={isHotelDropdownOpen}
        title='Konaklama Seçimi'
        onClose={() => setIsHotelDropdownOpen(false)}
        renderContent={renderHotelOptions}
        trigger={
          <SelectionTrigger
            type='button'
            $selected
            aria-haspopup='listbox'
            aria-expanded={isHotelDropdownOpen}
            disabled={hotels.length < 2}
            onClick={() => setIsHotelDropdownOpen((current) => !current)}
          >
            <SelectionField>
              <SelectionFieldLabel>Konaklama</SelectionFieldLabel>
              <SelectionLabel>
                {selectedHotel.hotelName || 'Konaklama'}
              </SelectionLabel>
            </SelectionField>
            {hotels.length > 1 ? (
              <SelectionChevron aria-hidden='true'>
                <CustomIcon
                  icon={ArrowDownSLine}
                  fontSize={20}
                  paletteColor='grey700'
                />
              </SelectionChevron>
            ) : null}
          </SelectionTrigger>
        }
      />
    </SelectionPanel>
  );
};

export default AlternativeStayOptionsList;
